import { PageHero } from "./Services";
import { BRAND } from "@/data/content";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: (
      <>
        <p>
          Petraloc collects information you voluntarily provide when interacting with our website,
          including your name, business email, phone number, company, project details, and any
          documents you choose to share for quotation purposes. We also collect limited technical
          information automatically (IP address, browser type, device, referring pages, and
          interaction patterns) to operate and secure the site.
        </p>
      </>
    ),
  },
  {
    title: "2. Contact Form Submissions",
    body: (
      <p>
        When you submit our contact, quote, or support forms, your message and the information you
        provide are stored in our secured database and used solely to respond to your inquiry,
        deliver requested services, and maintain a record of our communications. We do not sell,
        rent, or trade your contact details.
      </p>
    ),
  },
  {
    title: "3. Cookies and Analytics",
    body: (
      <p>
        We use a limited set of cookies and privacy-respectful analytics to understand site usage,
        improve performance, and detect abuse. You can disable cookies in your browser settings;
        the website will continue to function with minimal feature loss.
      </p>
    ),
  },
  {
    title: "4. Data Security",
    body: (
      <p>
        Petraloc applies industry-standard administrative, technical, and physical safeguards —
        including encrypted transit (TLS), restricted access controls, and audited storage — to
        protect the information you entrust to us. No method of transmission over the Internet is
        100% secure, but we continuously review our practices to align with enterprise expectations.
      </p>
    ),
  },
  {
    title: "5. Confidentiality",
    body: (
      <p>
        All client materials — source files, translations, glossaries, and project metadata — are
        treated as strictly confidential. Linguists, project managers, and engineers operate under
        signed non-disclosure agreements (NDAs) and are bound by Petraloc's internal information
        security policy.
      </p>
    ),
  },
  {
    title: "6. Third-Party Services",
    body: (
      <p>
        We rely on a small number of vetted third-party providers for hosting, email delivery, and
        analytics. Each provider is contractually obligated to handle data in line with applicable
        laws (including GDPR and CCPA where relevant). We never share your data with marketing or
        advertising networks.
      </p>
    ),
  },
  {
    title: "7. Your Rights",
    body: (
      <p>
        You may request access to, correction of, or deletion of the personal information we hold
        about you at any time. To exercise these rights, contact us at{" "}
        <a className="text-brand-blue underline" href={`mailto:${BRAND.email}`}>
          {BRAND.email}
        </a>
        . We respond to verified requests within thirty (30) days.
      </p>
    ),
  },
  {
    title: "8. Contact Information",
    body: (
      <p>
        Questions about this Privacy Policy or our data practices can be directed to{" "}
        <a className="text-brand-blue underline" href={`mailto:${BRAND.email}`}>
          {BRAND.email}
        </a>{" "}
        or by phone at {BRAND.phone}.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy"
        description="How Petraloc collects, uses, and protects the information you share with us."
        path="/privacy"
      />
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        subtitle="How Petraloc collects, uses, and protects the information you share with us."
      />

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5B6472] mb-8 font-semibold">
            Effective date · February 2026
          </p>
          <div className="space-y-9">
            {SECTIONS.map((s) => (
              <div key={s.title} data-testid={`privacy-section-${s.title.split(".")[0]}`}>
                <h2 className="text-xl lg:text-[22px] font-semibold text-[#071D49] mb-3 tracking-tight">
                  {s.title}
                </h2>
                <div className="text-[14.5px] text-[#222222] leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
